import type { FC } from 'react';

import { X } from 'lucide-react';
import { type UseFormReturn, useWatch } from 'react-hook-form';

import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
} from '@shared/components/ui';
import { GenericButton } from '@shared/components/ui/custom';
import { cn } from '@shared/utils';

import { type NewInvestigationFormValues, TARGET_TYPES } from './investigation.schema';

interface TargetRowProps {
  form: UseFormReturn<NewInvestigationFormValues>;
  index: number;
  onRemove: (index: number) => void;
  usedTypes: Set<string>;
  isLast: boolean;
}

export const TargetRow: FC<TargetRowProps> = ({ form, index, onRemove, usedTypes, isLast }) => {
  const currentType = useWatch({
    control: form.control,
    name: `targets.${index}.type`,
  });

  const getLabel = (type: string) => {
    switch (type) {
      case 'ip':
        return {
          label: 'Source IP Address',
          placeholder: 'Enter Source IP Address',
        };
      case 'host':
        return {
          label: 'Source Hostname',
          placeholder: 'Enter Source Hostname',
        };
      default:
        return {
          label: 'Username',
          placeholder: 'Enter Username',
        };
    }
  };

  return (
    <div
      className="space-y-3 animate-in fade-in slide-in-from-top-1"
      data-testid={`add-investigation-target-row-${index}`}
    >
      <FormField
        control={form.control}
        name={`targets.${index}.type`}
        render={({ field }) => (
          <FormItem className="space-y-0" data-testid={`add-investigation-target-type-formitem-${index}`}>
            <RadioGroup
              onValueChange={field.onChange}
              value={field.value}
              className="flex flex-row gap-6"
              data-testid={`add-investigation-target-type-group-${index}`}
            >
              {TARGET_TYPES.map((type) => {
                const isSelected = field.value === type;
                const isAvailable = !usedTypes.has(type);

                if (!isSelected && (!isLast || !isAvailable)) return null;

                return (
                  <div
                    key={type}
                    className="flex items-center gap-2"
                    data-testid={`add-investigation-target-type-option-${index}-${type}`}
                  >
                    <RadioGroupItem
                      value={type}
                      id={`${index}-${type}`}
                      data-testid={`add-investigation-target-type-radio-${index}-${type}`}
                    />
                    <Label
                      htmlFor={`${index}-${type}`}
                      className={cn(!isLast && 'cursor-default text-muted-foreground')}
                      data-testid={`add-investigation-target-type-label-${index}-${type}`}
                    >
                      {getLabel(type).label}
                    </Label>
                  </div>
                );
              })}
            </RadioGroup>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name={`targets.${index}.value`}
        render={({ field }) => (
          <FormItem data-testid={`add-investigation-target-value-formitem-${index}`}>
            <div className="flex items-center gap-4" data-testid={`add-investigation-target-value-row-${index}`}>
              <div className="relative flex-1" data-testid={`add-investigation-target-value-input-wrapper-${index}`}>
                <FormControl data-testid={`add-investigation-target-value-control-${index}`}>
                  <Input
                    {...field}
                    id={`add-investigation-target-value-input-${index}`}
                    aria-label="Investigation Target"
                    placeholder={getLabel(currentType).placeholder}
                    className={cn(index > 0 && 'pr-10')}
                    data-testid={`add-investigation-target-value-input-${index}`}
                  />
                </FormControl>

                {index > 0 && (
                  <GenericButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onRemove(index)}
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 hover:bg-muted text-muted-foreground"
                    icon={X}
                    data-testid={`add-investigation-target-remove-btn-${index}`}
                  />
                )}
              </div>

              {!isLast && (
                <span
                  className="text-muted-foreground text-xs font-semibold uppercase tracking-wider w-8 text-center"
                  data-testid={`add-investigation-target-or-label-${index}`}
                >
                  OR
                </span>
              )}
            </div>
            <FormMessage data-testid={`add-investigation-target-value-message-${index}`} />
          </FormItem>
        )}
      />
    </div>
  );
};
